import React, {
  useCallback, useEffect, useRef, useState
} from 'react';
import { useSnackbar } from 'notistack';

import { ReactComponent as IconConnectionLost } from '../../assets/icon/ic_connection_lost.svg';
import axiosInstance, { hasActiveApiRequests } from '../../utils/axios';

const HEALTH_CHECK_INTERVAL = 30000;
const HEALTH_CHECK_TIMEOUT = 5000;
const CONNECTION_ISSUES = {
  ELASTICSEARCH: 'elasticsearch',
  INTERNET: 'internet',
  MM: 'mm'
};
const CONNECTION_MESSAGES = {
  [CONNECTION_ISSUES.ELASTICSEARCH]: {
    description: 'MM API is online, but Elasticsearch is not responding.',
    title: 'Elasticsearch connection lost'
  },
  [CONNECTION_ISSUES.INTERNET]: {
    description: 'Check your network connection. The page will resume automatically.',
    title: 'No internet connection'
  },
  [CONNECTION_ISSUES.MM]: {
    description: 'MM API is not responding. We will keep trying to reconnect automatically.',
    title: 'Connection to MM lost'
  }
};

const ConnectionStatusMonitor = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [connectionIssue, setConnectionIssue] = useState(
    navigator.onLine ? null : CONNECTION_ISSUES.INTERNET
  );
  const isMmAvailableRef = useRef(null);
  const isMountedRef = useRef(true);

  const showInternetUnavailable = useCallback(() => {
    setConnectionIssue(CONNECTION_ISSUES.INTERNET);
  }, []);

  const checkMmAvailability = useCallback(async () => {
    if (!navigator.onLine || hasActiveApiRequests()) {
      return;
    }

    try {
      await axiosInstance.get('/health', {
        skipApiActivityTracking: true,
        timeout: HEALTH_CHECK_TIMEOUT,
      });

      if (!isMountedRef.current || !navigator.onLine) {
        return;
      }

      setConnectionIssue(null);

      if (isMmAvailableRef.current === false) {
        enqueueSnackbar('MM connection restored', { variant: 'success' });
      }

      isMmAvailableRef.current = true;
    } catch (error) {
      if (!isMountedRef.current || !navigator.onLine || hasActiveApiRequests()) {
        return;
      }

      const isElasticsearchUnavailable = error?.response?.data
        ?.services?.elasticsearch === 'unavailable';

      setConnectionIssue(
        isElasticsearchUnavailable ? CONNECTION_ISSUES.ELASTICSEARCH : CONNECTION_ISSUES.MM
      );
      isMmAvailableRef.current = false;
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    isMountedRef.current = true;

    const handleOffline = () => {
      isMmAvailableRef.current = null;
      showInternetUnavailable();
    };

    const handleOnline = () => {
      setConnectionIssue(null);
      enqueueSnackbar('Internet connection restored', { variant: 'success' });
      checkMmAvailability();
    };

    if (!navigator.onLine) {
      showInternetUnavailable();
    } else {
      checkMmAvailability();
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    const healthCheckInterval = window.setInterval(
      checkMmAvailability,
      HEALTH_CHECK_INTERVAL
    );

    return () => {
      isMountedRef.current = false;
      window.clearInterval(healthCheckInterval);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkMmAvailability, enqueueSnackbar, showInternetUnavailable]);

  if (!connectionIssue) {
    return null;
  }

  const isInternetUnavailable = connectionIssue === CONNECTION_ISSUES.INTERNET;
  const connectionMessage = CONNECTION_MESSAGES[connectionIssue];

  return (
    <div
      aria-describedby="connection-status-description"
      aria-labelledby="connection-status-title"
      aria-live="assertive"
      className="fixed inset-0 z-[9999] flex items-center justify-center
      bg-[rgba(13,27,42,.78)] px-[20px] backdrop-blur-[3px]"
      role="alertdialog"
    >
      <div className="flex w-full max-w-[460px]
      flex-col items-center rounded-[12px]
      bg-white px-[32px] py-[40px] text-center shadow-default dark:bg-background-dark"
      >
        <div className="mb-[24px] flex h-[104px]
        w-[104px] items-center justify-center
        rounded-full bg-danger-light text-danger dark:bg-darkscale-300"
        >
          <IconConnectionLost className="h-[64px] w-[64px]" />
        </div>
        <h2
          className="mb-[10px] text-[22px] font-semibold text-text-main dark:text-white"
          id="connection-status-title"
        >
          {connectionMessage.title}
        </h2>
        <p
          className="max-w-[360px] text-[14px] text-text-second dark:text-darkscale-100"
          id="connection-status-description"
        >
          {connectionMessage.description}
        </p>
        {!isInternetUnavailable && (
          <button
            className="mt-[28px] rounded-[6px] bg-primary px-[22px] py-[10px] text-[13px]
            font-medium text-white transition-colors hover:bg-primary-hover"
            onClick={checkMmAvailability}
            type="button"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
};

export default ConnectionStatusMonitor;
